import React, { useEffect, useState } from 'react';
import { Card, Table, Tag, Pagination, Modal, App, Space, Typography, Tooltip, Button, Form, Input, Upload, DatePicker, Radio, Drawer, List, Empty, Select } from 'antd';
import { StopOutlined, DeleteOutlined, ExclamationCircleOutlined, PlusOutlined, UploadOutlined, SendOutlined, PlayCircleOutlined, CaretRightOutlined, ReloadOutlined, EditOutlined } from '@ant-design/icons';
import { apiService } from '../services/api';
import { NewsletterWebDTO, ResponsePageNewsletterWebDTO, ActionResponseResponsePageNewsletterWebDTO, CreateNewsletterRequest, CreateWaNewsletterRequest, ResponsePageWebTemplateDTO, WebTemplateDTO, CreateTemplateRequest, Component, SenderDTO, AddSenderRequest } from '../types/auth';
import dayjs from 'dayjs';

const { Text } = Typography;

const Newsletter: React.FC = () => {
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [page, setPage] = useState<number>(0);
  const [size, setSize] = useState<number>(10);
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<ResponsePageNewsletterWebDTO>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 10,
  });
  const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false);
  const [isTestModalVisible, setIsTestModalVisible] = useState<boolean>(false);
  const [currentNewsletterId, setCurrentNewsletterId] = useState<string>('');
  const [testEmail, setTestEmail] = useState<string>('');
  const [createLoading, setCreateLoading] = useState<boolean>(false);
  const [testLoading, setTestLoading] = useState<boolean>(false);
  const [htmlFile, setHtmlFile] = useState<File | null>(null);
  const [recipientsFile, setRecipientsFile] = useState<File | null>(null);
  const [createTestEmail, setCreateTestEmail] = useState<string>('');
  const [createTestLoading, setCreateTestLoading] = useState<boolean>(false);
  const [formValid, setFormValid] = useState<boolean>(false);
  const [scheduledDate, setScheduledDate] = useState<dayjs.Dayjs | null>(null);
  const [newsletterType, setNewsletterType] = useState<'MAIL' | 'WA'>('MAIL');
  const [waScheduledDate, setWaScheduledDate] = useState<dayjs.Dayjs | null>(null);
  const [participantsFile, setParticipantsFile] = useState<File | null>(null);
  const [testPhones, setTestPhones] = useState<string>('');
  const [waTestLoading, setWaTestLoading] = useState<boolean>(false);
  const [isTemplatesDrawerVisible, setIsTemplatesDrawerVisible] = useState<boolean>(false);
  const [templates, setTemplates] = useState<ResponsePageWebTemplateDTO>({
    content: [],
    totalElements: 0,
    totalPages: 0,
    currentPage: 0,
    pageSize: 10,
  });
  const [templatesLoading, setTemplatesLoading] = useState<boolean>(false);
  const [templatesPage, setTemplatesPage] = useState<number>(1);
  const [templatesSize, setTemplatesSize] = useState<number>(10);
  const [isCreateTemplateModalVisible, setIsCreateTemplateModalVisible] = useState<boolean>(false);
  const [createTemplateLoading, setCreateTemplateLoading] = useState<boolean>(false);
  const [templateForm] = Form.useForm();
  const [isSendersDrawerVisible, setIsSendersDrawerVisible] = useState<boolean>(false);
  const [senders, setSenders] = useState<SenderDTO[]>([]);
  const [sendersLoading, setSendersLoading] = useState<boolean>(false);
  const [isAddSenderModalVisible, setIsAddSenderModalVisible] = useState<boolean>(false);
  const [isEditSenderModalVisible, setIsEditSenderModalVisible] = useState<boolean>(false);
  const [editingSender, setEditingSender] = useState<SenderDTO | null>(null);
  const [addSenderLoading, setAddSenderLoading] = useState<boolean>(false);
  const [editSenderLoading, setEditSenderLoading] = useState<boolean>(false);
  const [senderForm] = Form.useForm();
  const [editSenderForm] = Form.useForm();
  const [selectedSenders, setSelectedSenders] = useState<string[]>([]);

  const load = async (p = page, s = size) => {
    setLoading(true);
    try {
      const res: ActionResponseResponsePageNewsletterWebDTO = await apiService.getNewsletters(p, s);
      if (res.success && res.body) {
        setData(res.body);
      } else {
        message.error(res.msg || 'Failed to load newsletters');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.msg || 'Loading error');
    } finally {
      setLoading(false);
    }
  };

  const loadTemplates = async (p = templatesPage, s = templatesSize) => {
    setTemplatesLoading(true);
    try {
      const res = await apiService.getWaTemplates(p, s);
      setTemplates(res);
    } catch (e: any) {
      message.error(e?.response?.data?.msg || 'Failed to load templates');
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleRefreshTemplates = async () => {
    try {
      await apiService.refreshWaTemplatesList();
      message.success('Templates list refreshed');
      loadTemplates(templatesPage, templatesSize);
    } catch (e: any) {
      message.error(e?.response?.data?.msg || 'Failed to refresh templates');
    }
  };

  const loadSenders = async () => {
    setSendersLoading(true);
    try {
      const list = await apiService.getSenders();
      setSenders(list.filter((s) => s.type === 'MAIL'));
    } catch (e: any) {
      message.error(e?.response?.data?.msg || 'Failed to load senders');
    } finally {
      setSendersLoading(false);
    }
  };

  const handleAddSender = async () => {
    try {
      const values = await senderForm.validateFields();
      const request: AddSenderRequest = {
        sender: values.sender.trim(),
        title: values.title.trim(),
        authority: values.authority,
        type: 'MAIL',
      };
      setAddSenderLoading(true);
      await apiService.addSender(request);
      message.success('Sender added');
      setIsAddSenderModalVisible(false);
      senderForm.resetFields();
      loadSenders();
    } catch (e: any) {
      if (e.errorFields) return;
      message.error(e?.response?.data?.msg || 'Failed to add sender');
    } finally {
      setAddSenderLoading(false);
    }
  };

  const handleEditSender = async () => {
    if (!editingSender) return;
    try {
      const values = await editSenderForm.validateFields();
      setEditSenderLoading(true);
      await apiService.updateSender(editingSender.id, { title: values.title.trim() });
      message.success('Sender updated');
      setIsEditSenderModalVisible(false);
      setEditingSender(null);
      editSenderForm.resetFields();
      loadSenders();
    } catch (e: any) {
      if (e.errorFields) return;
      message.error(e?.response?.data?.msg || 'Failed to update sender');
    } finally {
      setEditSenderLoading(false);
    }
  };

  const confirmDeleteSender = (sender: SenderDTO) => {
    Modal.confirm({
      title: 'Delete Sender?',
      icon: <ExclamationCircleOutlined />,
      content: `${sender.title} (${sender.sender})`,
      okText: 'Delete',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      async onOk() {
        try {
          await apiService.deleteSender(sender.id);
          message.success('Sender deleted');
          loadSenders();
          setSelectedSenders((prev) => prev.filter((e) => e !== sender.sender));
        } catch (e: any) {
          message.error(e?.response?.data?.msg || 'Failed to delete sender');
        }
      },
    });
  };

  const openEditSenderModal = (sender: SenderDTO) => {
    setEditingSender(sender);
    editSenderForm.setFieldsValue({ title: sender.title });
    setIsEditSenderModalVisible(true);
  };

  const resetCreateModalState = () => {
    setIsCreateModalVisible(false);
    form.resetFields();
    setHtmlFile(null);
    setRecipientsFile(null);
    setCreateTestEmail('');
    setFormValid(false);
    setScheduledDate(null);
    setNewsletterType('MAIL');
    setParticipantsFile(null);
    setTestPhones('');
    setWaScheduledDate(null);
    setSelectedSenders([]);
  };

  const handleCreateTemplate = async () => {
    try {
      const values = await templateForm.validateFields();
      
      // Преобразуем компоненты из формы в нужный формат
      const components: Component[] = [];
      
      // HEADER
      if (values.headerFormat === 'IMAGE' && values.headerImageUrl) {
        components.push({
          type: 'HEADER',
          format: 'IMAGE',
          imageUrl: values.headerImageUrl,
        });
      } else if (values.headerText) {
        components.push({
          type: 'HEADER',
          format: 'TEXT',
          text: values.headerText,
        });
      }
      
      // BODY
      if (values.bodyText) {
        components.push({
          type: 'BODY',
          format: 'TEXT',
          text: values.bodyText,
        });
      }
      
      // FOOTER
      if (values.footerText) {
        components.push({
          type: 'FOOTER',
          format: 'TEXT',
          text: values.footerText,
        });
      }
      
      // BUTTONS
      if (values.buttonText && values.buttonLink) {
        components.push({
          type: 'BUTTONS',
          format: 'TEXT',
          buttons: [{
            type: 'URL',
            text: values.buttonText,
            link: values.buttonLink,
          }],
        });
      }

      if (components.length === 0) {
        message.error('Please add at least one component');
        return;
      }

      const request: CreateTemplateRequest = {
        name: values.name,
        language: values.language,
        category: values.category || 'MARKETING',
        components,
        status: 'PENDING',
        isApproved: false,
        isRejected: false,
      };

      setCreateTemplateLoading(true);
      const res = await apiService.createWaTemplate(request);
      if (res.success) {
        message.success('Template created successfully');
        setIsCreateTemplateModalVisible(false);
        templateForm.resetFields();
        loadTemplates(templatesPage, templatesSize);
      } else {
        message.error(res.msg || 'Failed to create template');
      }
    } catch (e: any) {
      if (e.errorFields) {
        // Validation errors
        return;
      }
      message.error(e?.response?.data?.msg || 'Failed to create template');
    } finally {
      setCreateTemplateLoading(false);
    }
  };

  useEffect(() => {
    load(0, size);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const confirmStop = (row: NewsletterWebDTO) => {
    Modal.confirm({
      title: 'Stop Newsletter?',
      icon: <ExclamationCircleOutlined />,
      content: `Subject: ${row.topic}`,
      okText: 'Stop',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      async onOk() {
        try {
          await apiService.stopNewsletter(row.id);
          message.success('Newsletter stopped');
          load();
        } catch (e: any) {
          message.error(e?.response?.data?.msg || 'Failed to stop');
        }
      },
    });
  };

  const confirmContinue = (row: NewsletterWebDTO) => {
    Modal.confirm({
      title: 'Continue Newsletter?',
      icon: <ExclamationCircleOutlined />,
      content: `Subject: ${row.topic}`,
      okText: 'Continue',
      cancelText: 'Cancel',
      async onOk() {
        try {
          await apiService.continueNewsletter(row.id);
          message.success('Newsletter continued');
          load();
        } catch (e: any) {
          message.error(e?.response?.data?.msg || 'Failed to continue');
        }
      },
    });
  };

  const confirmDelete = (row: NewsletterWebDTO) => {
    Modal.confirm({
      title: 'Delete Newsletter?',
      icon: <ExclamationCircleOutlined />,
      content: `Subject: ${row.topic}`,
      okText: 'Delete',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      async onOk() {
        try {
          const res = await apiService.deleteNewsletter(row.id);
          if (res?.success !== false) {
            message.success('Newsletter deleted');
            load();
          } else {
            message.error(res?.msg || 'Failed to delete');
          }
        } catch (e: any) {
          message.error(e?.response?.data?.msg || 'Failed to delete');
        }
      },
    });
  };

  const handleCreateNewsletter = async () => {
    if (newsletterType === 'MAIL') {
      if (!htmlFile || !recipientsFile) {
        message.error('Please select HTML file and recipients file');
        return;
      }
      if (selectedSenders.length === 0) {
        message.error('Please select at least one sender email');
        return;
      }

      setCreateLoading(true);
      try {
        const values = await form.validateFields();
        const request: CreateNewsletterRequest = {
          topic: values.topic,
          htmlFile,
          recipientsFile,
          senders: selectedSenders.join(','),
          scheduledDate: scheduledDate ? scheduledDate.toISOString() : undefined,
        };

        const res = await apiService.createNewsletter(request);
        if (res.success) {
          message.success('Newsletter created successfully');
          resetCreateModalState();
          load();
        } else {
          message.error(res.msg || 'Failed to create newsletter');
        }
      } catch (e: any) {
        message.error(e?.response?.data?.msg || 'Newsletter creation error');
      } finally {
        setCreateLoading(false);
      }
    } else {
      // WA newsletter
      if (!participantsFile) {
        message.error('Please select participants file');
        return;
      }

      setCreateLoading(true);
      try {
        const values = await form.validateFields();
        const request: CreateWaNewsletterRequest = {
          description: values.description,
          templateId: values.templateId,
          templateLang: values.templateLang,
          imageUrl: values.imageUrl || undefined,
          participantsFile,
          scheduledDate: waScheduledDate ? waScheduledDate.toISOString() : undefined,
        };

        const res = await apiService.createWaNewsletter(request);
        if (res.success) {
          message.success('WA Newsletter created successfully');
          setIsCreateModalVisible(false);
          form.resetFields();
          setParticipantsFile(null);
          setTestPhones('');
          setWaScheduledDate(null);
          setNewsletterType('MAIL');
          load();
        } else {
          message.error(res.msg || 'Failed to create WA newsletter');
        }
      } catch (e: any) {
        message.error(e?.response?.data?.msg || 'WA Newsletter creation error');
      } finally {
        setCreateLoading(false);
      }
    }
  };

  const handleWaTest = async () => {
    if (!participantsFile) {
      message.error('Please select participants file');
      return;
    }

    if (!testPhones || testPhones.trim() === '') {
      message.error('Please enter test phones');
      return;
    }

    setWaTestLoading(true);
    try {
      const values = await form.validateFields();
      const request: CreateWaNewsletterRequest = {
        description: values.description,
        templateId: values.templateId,
        templateLang: values.templateLang,
        imageUrl: values.imageUrl || undefined,
        participantsFile,
        scheduledDate: waScheduledDate ? waScheduledDate.toISOString() : undefined,
        testPhones: testPhones.trim(),
      };

      const res = await apiService.createWaNewsletter(request);
      if (res.success) {
        message.success('Test sent');
      } else {
        message.error(res.msg || 'Failed to send test');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.msg || 'Test sending error');
    } finally {
      setWaTestLoading(false);
    }
  };

  const handleTestNewsletter = async () => {
    if (!testEmail || testEmail.trim() === '') {
      message.error('Please enter email for testing');
      return;
    }

    setTestLoading(true);
    try {
      console.log('Sending test email request:', {
        newsletterId: currentNewsletterId,
        testEmail: testEmail.trim()
      });
      
      const res = await apiService.testExistingNewsletter({
        newsletterId: currentNewsletterId,
        testEmail: testEmail.trim(),
      });
      
      if (res.success) {
        message.success('Test sent');
      } else {
        message.error(res.msg || 'Failed to send test email');
      }
    } catch (e: any) {
      console.error('Test email error:', e);
      message.error(e?.response?.data?.msg || 'Test email sending error');
    } finally {
      setTestLoading(false);
    }
  };

  const confirmLaunch = (row: NewsletterWebDTO) => {
    Modal.confirm({
      title: 'Launch Newsletter?',
      icon: <ExclamationCircleOutlined />,
      content: `Subject: ${row.topic}\n\nThis action cannot be undone. Continue?`,
      okText: 'Launch',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      async onOk() {
        try {
          const res = await apiService.launchNewsletter({ newsletterId: row.id });
          if (res.success) {
            message.success('Newsletter launched');
            load();
          } else {
            message.error(res.msg || 'Failed to launch newsletter');
          }
        } catch (e: any) {
          message.error(e?.response?.data?.msg || 'Failed to launch newsletter');
        }
      },
    });
  };

  const confirmSendNow = (row: NewsletterWebDTO) => {
    Modal.confirm({
      title: 'Send Newsletter Now?',
      icon: <ExclamationCircleOutlined />,
      content: `Subject: ${row.topic}\n\nThis will send the newsletter immediately instead of waiting for the scheduled time. Continue?`,
      okText: 'Send Now',
      cancelText: 'Cancel',
      okButtonProps: { danger: true },
      async onOk() {
        try {
          const res = await apiService.launchNewsletter({ newsletterId: row.id });
          if (res.success) {
            message.success('Newsletter sent now');
            load();
          } else {
            message.error(res.msg || 'Failed to send newsletter');
          }
        } catch (e: any) {
          message.error(e?.response?.data?.msg || 'Failed to send newsletter');
        }
      },
    });
  };

  const openTestModal = (newsletterId: string) => {
    setCurrentNewsletterId(newsletterId);
    setTestEmail(''); // Очищаем поле email при открытии модального окна
    setIsTestModalVisible(true);
  };

  const handleCreateTestNewsletter = async () => {
    if (!htmlFile || !recipientsFile) {
      message.error('Please select HTML file and recipients file');
      return;
    }

    if (!createTestEmail) {
      message.error('Please enter email for testing');
      return;
    }
    if (selectedSenders.length === 0) {
      message.error('Please select at least one sender email');
      return;
    }

    setCreateTestLoading(true);
    try {
      const values = await form.validateFields();
      
      // Отправляем тестовое письмо напрямую
      const testRes = await apiService.testNewsletter({
        topic: values.topic,
        testEmail: createTestEmail,
        htmlFile,
        recipientsFile,
        senders: selectedSenders.join(','),
      });
      
      if (testRes.success) {
        message.success('Test email sent');
      } else {
        message.error(testRes.msg || 'Failed to send test email');
      }
    } catch (e: any) {
      message.error(e?.response?.data?.msg || 'Test email sending error');
    } finally {
      setCreateTestLoading(false);
    }
  };

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 200,
      render: (v: string) => <Text code>{v}</Text>,
    },
    {
      title: 'Subject',
      dataIndex: 'topic',
      key: 'topic',
      ellipsis: false,
      render: (v: string) => (
        <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{v}</Text>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (type: 'MAIL' | 'WA' | undefined) => {
        if (!type) return <Text type="secondary">-</Text>;
        const color = type === 'MAIL' ? 'blue' : 'green';
        return <Tag color={color}>{type}</Tag>;
      },
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 180,
      render: (v: string) => new Date(v).toLocaleString(),
    },
    {
      title: 'Scheduled',
      dataIndex: 'scheduledDate',
      key: 'scheduledDate',
      width: 180,
      render: (v: string | null) => {
        if (!v) return <Text type="secondary">-</Text>;
        return <Text>{new Date(v).toLocaleString()}</Text>;
      },
    },
    {
      title: 'Status',
      dataIndex: 'state',
      key: 'state',
      width: 120,
      render: (s: NewsletterWebDTO['state']) => {
        const color = s === 'ACTIVE' ? 'green' : s === 'DRAFT' ? 'blue' : s === 'SCHEDULED' ? 'purple' : 'orange';
        return <Tag color={color}>{s}</Tag>;
      },
    },
    {
      title: 'Sent',
      dataIndex: 'sent',
      key: 'sent',
      width: 110,
      render: (v: number) => <Text strong>{v}</Text>,
    },
    {
      title: 'Total',
      dataIndex: 'total',
      key: 'total',
      width: 100,
      render: (v: number) => <Text>{v}</Text>,
    },
    {
      title: 'Errors',
      dataIndex: 'error',
      key: 'error',
      width: 100,
      render: (v: number) => <Text type="danger">{v}</Text>,
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 280,
      render: (_: any, r: NewsletterWebDTO) => (
        <Space>
          {r.state === 'DRAFT' && (
            <>
              <Tooltip title="Test">
                <Button
                  type="default"
                  icon={<SendOutlined />}
                  onClick={() => openTestModal(r.id)}
                />
              </Tooltip>
              <Tooltip title="Launch">
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={() => confirmLaunch(r)}
                />
              </Tooltip>
            </>
          )}
          {r.state === 'SCHEDULED' && (
            <>
              <Tooltip title="Test">
                <Button
                  type="default"
                  icon={<SendOutlined />}
                  onClick={() => openTestModal(r.id)}
                />
              </Tooltip>
              <Tooltip title="Send Now">
                <Button
                  type="primary"
                  icon={<PlayCircleOutlined />}
                  onClick={() => confirmSendNow(r)}
                />
              </Tooltip>
            </>
          )}
          {r.state === 'ACTIVE' && (
            <Tooltip title="Stop">
              <Button
                type="default"
                icon={<StopOutlined />}
                onClick={() => confirmStop(r)}
              />
            </Tooltip>
          )}
          {r.state === 'STOPPED' && (
            <Tooltip title="Continue">
              <Button
                type="primary"
                icon={<CaretRightOutlined />}
                onClick={() => confirmContinue(r)}
              />
            </Tooltip>
          )}
          <Tooltip title="Delete">
            <Button
              danger
              type="primary"
              icon={<DeleteOutlined />}
              onClick={() => confirmDelete(r)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Card 
        title="Newsletter" 
        bordered={false}
        extra={
          <Space>
            <Button
              onClick={() => {
                setIsSendersDrawerVisible(true);
                loadSenders();
              }}
            >
              Senders
            </Button>
            <Button 
              onClick={() => {
                setIsTemplatesDrawerVisible(true);
                loadTemplates(1, 10);
              }}
            >
              Wa Templates
            </Button>
            <Button 
              type="primary" 
              icon={<PlusOutlined />}
              onClick={() => {
                setIsCreateModalVisible(true);
                loadSenders();
              }}
            >
              Create New
            </Button>
          </Space>
        }
      >
        <Table
          rowKey={(r) => r.id}
          loading={loading}
          dataSource={data.content}
          columns={columns as any}
          pagination={false}
          size="middle"
        />
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
          <Pagination
            current={data.currentPage + 1}
            pageSize={data.pageSize}
            total={data.totalElements}
            showSizeChanger
            pageSizeOptions={[10, 20, 50]}
            onChange={(cp, ps) => {
              const newPage = cp - 1;
              setPage(newPage);
              setSize(ps);
              load(newPage, ps);
            }}
          />
        </div>
      </Card>

      {/* Create newsletter modal */}
      <Modal
        title="Create New Newsletter"
        open={isCreateModalVisible}
        onCancel={resetCreateModalState}
        footer={[
          <Button key="cancel" onClick={resetCreateModalState}>
            Cancel
          </Button>,
          <Button 
            key="create" 
            type="primary" 
            loading={createLoading}
            onClick={handleCreateNewsletter}
            disabled={
              newsletterType === 'MAIL' 
                ? (!htmlFile || !recipientsFile || !formValid || selectedSenders.length === 0)
                : (!participantsFile || !form.getFieldValue('templateId') || !form.getFieldValue('templateLang') || !form.getFieldValue('description'))
            }
          >
            Create
          </Button>,
        ]}
        width={600}
      >
        <Form 
          form={form} 
          layout="vertical"
          onValuesChange={(changedValues, allValues) => {
            if (newsletterType === 'MAIL') {
              setFormValid(!!allValues.topic && allValues.topic.trim() !== '');
            }
          }}
        >
          <Form.Item
            label="Newsletter Type"
            required
          >
            <Radio.Group 
              value={newsletterType} 
              onChange={(e) => {
                setNewsletterType(e.target.value);
                form.resetFields();
                setHtmlFile(null);
                setRecipientsFile(null);
                setParticipantsFile(null);
                setCreateTestEmail('');
                setTestPhones('');
                setScheduledDate(null);
                setWaScheduledDate(null);
                setFormValid(false);
                setSelectedSenders([]);
              }}
            >
              <Radio value="MAIL">Email</Radio>
              <Radio value="WA">WA</Radio>
            </Radio.Group>
          </Form.Item>

          {newsletterType === 'MAIL' ? (
            <>
              <Form.Item
                name="topic"
                label="Newsletter Subject"
                rules={[{ required: true, message: 'Please enter newsletter subject' }]}
              >
                <Input placeholder="Enter newsletter subject" />
              </Form.Item>

              <Form.Item
                label="From Emails"
                required
                help="Select one or more sender emails for this newsletter"
              >
                <Select
                  mode="multiple"
                  placeholder="Select sender emails"
                  value={selectedSenders}
                  onChange={setSelectedSenders}
                  loading={sendersLoading}
                  options={senders.map((s) => ({
                    value: s.sender,
                    label: `${s.title} (${s.sender})`,
                  }))}
                  notFoundContent={sendersLoading ? 'Loading...' : 'No MAIL senders. Add them in Senders.'}
                />
              </Form.Item>

              <Form.Item
                label="Scheduled Date & Time (Optional)"
                help="Leave empty to send immediately, or select a date/time to schedule the newsletter"
              >
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="Select date and time for scheduled sending"
                  value={scheduledDate}
                  onChange={(date) => setScheduledDate(date)}
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                label="HTML File"
                required
              >
                <Upload
                  accept=".html,.htm"
                  beforeUpload={(file) => {
                    const isHtml = file.type === 'text/html' || 
                                  file.name.toLowerCase().endsWith('.html') || 
                                  file.name.toLowerCase().endsWith('.htm');
                    if (!isHtml) {
                      message.error('Please select HTML file (.html or .htm)');
                      return false;
                    }
                    setHtmlFile(file);
                    return false;
                  }}
                  onRemove={() => setHtmlFile(null)}
                  fileList={htmlFile ? [{
                    uid: '1',
                    name: htmlFile.name,
                    status: 'done',
                  }] : []}
                  showUploadList={{
                    showRemoveIcon: true,
                    showDownloadIcon: false,
                    showPreviewIcon: false,
                  }}
                >
                  <Button icon={<UploadOutlined />}>Select HTML File</Button>
                </Upload>
              </Form.Item>

              <Form.Item
                label="Recipients File (XLSX/CSV)"
                required
              >
                <Upload
                  accept=".xlsx,.xls,.csv"
                  beforeUpload={(file) => {
                    const fileName = file.name.toLowerCase();
                    const isExcel = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
                                  file.type === 'application/vnd.ms-excel' ||
                                  fileName.endsWith('.xlsx') || 
                                  fileName.endsWith('.xls');
                    const isCsv = file.type === 'text/csv' || 
                                 fileName.endsWith('.csv');
                    if (!isExcel && !isCsv) {
                      message.error('Please select XLSX, XLS or CSV file');
                      return false;
                    }
                    setRecipientsFile(file);
                    return false;
                  }}
                  onRemove={() => setRecipientsFile(null)}
                  fileList={recipientsFile ? [{
                    uid: '2',
                    name: recipientsFile.name,
                    status: 'done',
                  }] : []}
                  showUploadList={{
                    showRemoveIcon: true,
                    showDownloadIcon: false,
                    showPreviewIcon: false,
                  }}
                >
                  <Button icon={<UploadOutlined />}>Select Recipients File</Button>
                </Upload>
              </Form.Item>

              <Form.Item
                label="Test Email"
                help="Enter email for sending test email (optional)"
              >
                <Input.Group compact>
                  <Input
                    type="email"
                    placeholder="Enter email for testing"
                    value={createTestEmail}
                    onChange={(e) => setCreateTestEmail(e.target.value)}
                    style={{ width: 'calc(100% - 80px)' }}
                  />
                  <Button 
                    type="default" 
                    icon={<SendOutlined />}
                    loading={createTestLoading}
                    onClick={handleCreateTestNewsletter}
                    disabled={!htmlFile || !recipientsFile || !createTestEmail || !formValid || selectedSenders.length === 0}
                    style={{ width: '80px' }}
                  >
                    Test
                  </Button>
                </Input.Group>
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item
                name="description"
                label="Description"
                rules={[{ required: true, message: 'Please enter description' }]}
              >
                <Input.TextArea placeholder="Enter description" rows={3} />
              </Form.Item>

              <Form.Item
                name="templateId"
                label="Template ID"
                rules={[{ required: true, message: 'Please enter template ID' }]}
              >
                <Input placeholder="Enter template ID" />
              </Form.Item>

              <Form.Item
                name="templateLang"
                label="Template Language"
                rules={[{ required: true, message: 'Please enter template language' }]}
              >
                <Input placeholder="Enter template language" />
              </Form.Item>

              <Form.Item
                name="imageUrl"
                label="Image URL"
              >
                <Input placeholder="Enter image URL" />
              </Form.Item>

              <Form.Item
                label="Scheduled Date & Time (Optional)"
                help="Leave empty to send immediately, or select a date/time to schedule the newsletter"
              >
                <DatePicker
                  showTime
                  format="YYYY-MM-DD HH:mm:ss"
                  placeholder="Select date and time for scheduled sending"
                  value={waScheduledDate}
                  onChange={(date) => setWaScheduledDate(date)}
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                  style={{ width: '100%' }}
                />
              </Form.Item>

              <Form.Item
                label="Participants (CSV File)"
                required
              >
                <Upload
                  accept=".csv"
                  beforeUpload={(file) => {
                    const fileName = file.name.toLowerCase();
                    const isCsv = file.type === 'text/csv' || 
                                 fileName.endsWith('.csv');
                    if (!isCsv) {
                      message.error('Please select CSV file');
                      return false;
                    }
                    setParticipantsFile(file);
                    return false;
                  }}
                  onRemove={() => setParticipantsFile(null)}
                  fileList={participantsFile ? [{
                    uid: '3',
                    name: participantsFile.name,
                    status: 'done',
                  }] : []}
                  showUploadList={{
                    showRemoveIcon: true,
                    showDownloadIcon: false,
                    showPreviewIcon: false,
                  }}
                >
                  <Button icon={<UploadOutlined />}>Select Participants CSV File</Button>
                </Upload>
              </Form.Item>

              <Form.Item
                label="Test Phones"
                help="Enter phone numbers separated by comma for testing (optional)"
              >
                <Input.Group compact>
                  <Input
                    placeholder="Enter test phones (comma-separated)"
                    value={testPhones}
                    onChange={(e) => setTestPhones(e.target.value)}
                    style={{ width: 'calc(100% - 80px)' }}
                  />
                  <Button 
                    type="default" 
                    icon={<SendOutlined />}
                    loading={waTestLoading}
                    onClick={handleWaTest}
                    disabled={!participantsFile || !form.getFieldValue('templateId') || !form.getFieldValue('templateLang') || !form.getFieldValue('description') || !testPhones || testPhones.trim() === ''}
                    style={{ width: '80px' }}
                  >
                    Test
                  </Button>
                </Input.Group>
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>

      {/* Test modal */}
      <Modal
        title="Test Newsletter"
        open={isTestModalVisible}
        onOk={handleTestNewsletter}
        onCancel={() => {
          setIsTestModalVisible(false);
          setTestEmail('');
        }}
        confirmLoading={testLoading}
      >
        <Form layout="vertical">
          <Form.Item
            label="Test Email"
            required
          >
            <Input
              type="email"
              placeholder="Enter email for sending test email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* WA Templates Drawer */}
      <Drawer
        title="WA Templates"
        placement="right"
        width={600}
        open={isTemplatesDrawerVisible}
        onClose={() => setIsTemplatesDrawerVisible(false)}
        extra={
          <Space>
            <Button 
              icon={<ReloadOutlined />}
              onClick={handleRefreshTemplates}
              loading={templatesLoading}
            >
              Refresh
            </Button>
            <Button 
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsCreateTemplateModalVisible(true)}
            >
              Create New
            </Button>
          </Space>
        }
      >
        <List
          loading={templatesLoading}
          dataSource={templates.content}
          locale={{ emptyText: <Empty description="No templates found" /> }}
          renderItem={(item: WebTemplateDTO) => (
            <List.Item>
              <List.Item.Meta
                title={item.name}
                description={
                  <Space direction="vertical" size="small" style={{ width: '100%' }}>
                    <div>
                      <Tag color={item.status === 'APPROVED' ? 'green' : item.status === 'REJECTED' ? 'red' : 'orange'}>
                        {item.status}
                      </Tag>
                      <Tag color="blue">{item.language}</Tag>
                    </div>
                    <div style={{ fontSize: 12, color: '#999' }}>
                      Created: {new Date(item.createdAt).toLocaleString()}
                    </div>
                  </Space>
                }
              />
            </List.Item>
          )}
        />
        {templates.totalElements > 0 && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
            <Pagination
              current={templates.currentPage}
              pageSize={templates.pageSize}
              total={templates.totalElements}
              showSizeChanger
              pageSizeOptions={[10, 20, 50]}
              onChange={(cp, ps) => {
                setTemplatesPage(cp);
                setTemplatesSize(ps);
                loadTemplates(cp, ps);
              }}
            />
          </div>
        )}
      </Drawer>

      {/* Create Template Modal */}
      <Modal
        title="Create New WA Template"
        open={isCreateTemplateModalVisible}
        onCancel={() => {
          setIsCreateTemplateModalVisible(false);
          templateForm.resetFields();
        }}
        footer={[
          <Button key="cancel" onClick={() => {
            setIsCreateTemplateModalVisible(false);
            templateForm.resetFields();
          }}>
            Cancel
          </Button>,
          <Button 
            key="create" 
            type="primary" 
            loading={createTemplateLoading}
            onClick={handleCreateTemplate}
          >
            Create
          </Button>,
        ]}
        width={700}
      >
        <Form 
          form={templateForm} 
          layout="vertical"
        >
          <Form.Item
            name="name"
            label="Template Name"
            rules={[{ required: true, message: 'Please enter template name' }]}
          >
            <Input placeholder="Enter template name" />
          </Form.Item>

          <Form.Item
            name="language"
            label="Language"
            rules={[{ required: true, message: 'Please enter language code (e.g., en_US, ru_RU)' }]}
          >
            <Input placeholder="Enter language code (e.g., en_US, ru_RU, ar)" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            initialValue="MARKETING"
          >
            <Input placeholder="Enter category (default: MARKETING)" />
          </Form.Item>

          <Form.Item
            name="headerFormat"
            label="Header Format"
            initialValue="TEXT"
          >
            <Radio.Group>
              <Radio value="TEXT">Text</Radio>
              <Radio value="IMAGE">Image</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            noStyle
            shouldUpdate={(prevValues, currentValues) => prevValues.headerFormat !== currentValues.headerFormat}
          >
            {({ getFieldValue }) =>
              getFieldValue('headerFormat') === 'TEXT' ? (
                <Form.Item
                  name="headerText"
                  label="Header Text (Optional)"
                >
                  <Input.TextArea placeholder="Enter header text" rows={2} />
                </Form.Item>
              ) : (
                <Form.Item
                  name="headerImageUrl"
                  label="Header Image URL (Required for IMAGE format)"
                  rules={[
                    {
                      required: getFieldValue('headerFormat') === 'IMAGE',
                      message: 'Please enter image URL',
                    },
                    {
                      type: 'url',
                      message: 'Please enter a valid URL',
                    },
                  ]}
                >
                  <Input placeholder="Enter image URL (must be publicly accessible HTTPS URL)" />
                </Form.Item>
              )
            }
          </Form.Item>

          <Form.Item
            name="bodyText"
            label="Body Text (Optional)"
          >
            <Input.TextArea placeholder="Enter body text" rows={4} />
          </Form.Item>

          <Form.Item
            name="footerText"
            label="Footer Text (Optional)"
          >
            <Input.TextArea placeholder="Enter footer text" rows={2} />
          </Form.Item>

          <Form.Item
            name="buttonText"
            label="Button Text (Optional)"
          >
            <Input placeholder="Enter button text" />
          </Form.Item>

          <Form.Item
            name="buttonLink"
            label="Button Link"
            dependencies={['buttonText']}
            rules={[
              ({ getFieldValue }) => ({
                validator(_, value) {
                  const buttonText = getFieldValue('buttonText');
                  if (buttonText && !value) {
                    return Promise.reject(new Error('Button link is required when button text is provided'));
                  }
                  return Promise.resolve();
                },
              }),
              {
                type: 'url',
                message: 'Please enter a valid URL',
              },
            ]}
          >
            <Input placeholder="Enter button URL (required if button text is provided)" />
          </Form.Item>

          <div style={{ marginTop: 16, padding: 12, background: '#f5f5f5', borderRadius: 4 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Note: At least one component (Header, Body, Footer, or Button) must be filled.
            </Text>
          </div>
        </Form>
      </Modal>

      {/* Senders Drawer */}
      <Drawer
        title="Senders (MAIL)"
        placement="right"
        width={700}
        open={isSendersDrawerVisible}
        onClose={() => setIsSendersDrawerVisible(false)}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setIsAddSenderModalVisible(true)}
          >
            Add
          </Button>
        }
      >
        <Table
          rowKey="id"
          loading={sendersLoading}
          dataSource={senders}
          pagination={false}
          size="middle"
          locale={{ emptyText: <Empty description="No MAIL senders" /> }}
          columns={[
            {
              title: 'Email',
              dataIndex: 'sender',
              key: 'sender',
              render: (v: string) => <Text code>{v}</Text>,
            },
            {
              title: 'Title',
              dataIndex: 'title',
              key: 'title',
            },
            {
              title: 'Actions',
              key: 'actions',
              width: 120,
              render: (_: unknown, record: SenderDTO) => (
                <Space>
                  <Tooltip title="Edit title">
                    <Button
                      type="default"
                      icon={<EditOutlined />}
                      onClick={() => openEditSenderModal(record)}
                    />
                  </Tooltip>
                  <Tooltip title="Delete">
                    <Button
                      danger
                      type="primary"
                      icon={<DeleteOutlined />}
                      onClick={() => confirmDeleteSender(record)}
                    />
                  </Tooltip>
                </Space>
              ),
            },
          ]}
        />
      </Drawer>

      {/* Add Sender Modal */}
      <Modal
        title="Add Sender"
        open={isAddSenderModalVisible}
        onCancel={() => {
          setIsAddSenderModalVisible(false);
          senderForm.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsAddSenderModalVisible(false);
              senderForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="add" type="primary" loading={addSenderLoading} onClick={handleAddSender}>
            Add
          </Button>,
        ]}
      >
        <Form form={senderForm} layout="vertical">
          <Form.Item
            name="sender"
            label="Email"
            rules={[
              { required: true, message: 'Please enter sender email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="sender@example.com" />
          </Form.Item>
          <Form.Item
            name="title"
            label="Display Name"
            rules={[{ required: true, message: 'Please enter display name' }]}
          >
            <Input placeholder="Mira Developments" />
          </Form.Item>
          <Form.Item
            name="authority"
            label="App Password"
            rules={[{ required: true, message: 'Please enter app password' }]}
          >
            <Input.Password placeholder="SMTP app password" />
          </Form.Item>
        </Form>
      </Modal>

      {/* Edit Sender Modal */}
      <Modal
        title="Edit Sender"
        open={isEditSenderModalVisible}
        onCancel={() => {
          setIsEditSenderModalVisible(false);
          setEditingSender(null);
          editSenderForm.resetFields();
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsEditSenderModalVisible(false);
              setEditingSender(null);
              editSenderForm.resetFields();
            }}
          >
            Cancel
          </Button>,
          <Button key="save" type="primary" loading={editSenderLoading} onClick={handleEditSender}>
            Save
          </Button>,
        ]}
      >
        {editingSender && (
          <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
            Email: <Text code>{editingSender.sender}</Text>
          </Text>
        )}
        <Form form={editSenderForm} layout="vertical">
          <Form.Item
            name="title"
            label="Display Name"
            rules={[{ required: true, message: 'Please enter display name' }]}
          >
            <Input placeholder="Display name" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default Newsletter;
